import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ChatMessage, ChatState, AgentAction } from './chat.ts';
import { generateChatResponse } from './openai.ts';
import { AgentService } from '../../services/agentService.ts';
import { globalRefreshEmitter } from '../../hooks/useAgentIntegration.ts';
import ConversationHistoryService, { HistoryMessage } from '../../services/conversationHistoryService.ts';
import { navigationService } from '../../services/navigationService.ts';
import confetti from 'canvas-confetti';

interface ChatStore extends ChatState {
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  sendMessage: (text: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  executeAction: (action: AgentAction) => Promise<void>;
  clearPendingActions: () => void;
  loadConversationHistory: () => Promise<void>;
  isHistoryLoaded: boolean;
  isHistoryLoading: boolean;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
  messages: [{
    id: 'welcome',
    text: "Hi! I'm your wedding planning assistant. I can help you schedule appointments, create tasks, manage your budget, and navigate your planning dashboard. What would you like to do? 💝",
    isUser: false,
    timestamp: Date.now(),
  }],
  isLoading: false,
  error: null,
  pendingActions: [],
  isHistoryLoaded: false,
  isHistoryLoading: false,

  addMessage: (message) => set((state) => {
    // Generate a unique ID to prevent duplicate keys
    const now = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const uniqueId = `${now}-${randomSuffix}`;
    
    return {
      messages: [...state.messages, {
        id: uniqueId,
        timestamp: now,
        ...message,
      }],
    };
  }),

  sendMessage: async (text: string) => {
    const store = get();
    console.log('🚀 Starting message send process:', text);
    
    try {
      console.log('🔧 Setting loading state and clearing errors...');
      store.setLoading(true);
      store.setError(null);

      // Add user message immediately for better UX
      console.log('💬 Adding user message to chat...');
      store.addMessage({ text, isUser: true });

      // Get AI response from backend - backend handles all data operations
      console.log('🌐 Calling generateChatResponse API...');
      const response = await generateChatResponse(text);
      console.log('📥 Chat response received:', {
        response,
        hasText: !!response.text,
        textLength: response.text?.length,
        hasActions: !!response.actions,
        actionsCount: response.actions?.length || 0,
        hasData: !!response.data
      });
      
      // Add AI response
      console.log('💬 Adding AI response to chat:', response.text?.substring(0, 100) + '...');
      store.addMessage({ text: response.text, isUser: false });
      
      // Check if response contains vendor search results - check actions first, then data
      let vendors: any[] = [];
      let isVendorSearch = false;
      
      console.log('🔍 Checking for vendor search results...');
      
      // First check if there's a vendor action with search_vendors
      if (response.actions && response.actions.length > 0) {
        console.log('🎯 Found actions in response:', response.actions.map(a => ({ type: a.type, hasData: !!a.data })));
        const vendorAction = response.actions.find(action => action.type === 'vendor');
        if (vendorAction) {
          console.log('🏪 Found vendor action:', {
            hasData: !!vendorAction.data,
            hasBackendResponse: !!vendorAction.data?.backendResponse,
            actionPerformed: vendorAction.data?.backendResponse?.actionPerformed
          });
          if (vendorAction.data?.backendResponse?.actionPerformed === 'search_vendors') {
            isVendorSearch = true;
            if (vendorAction.data.backendResponse.data && Array.isArray(vendorAction.data.backendResponse.data)) {
              vendors = vendorAction.data.backendResponse.data;
              console.log('✅ Found vendors in action:', vendors.length);
            }
          }
        }
      } else {
        console.log('🎯 No actions found in response');
      }
      
      // Fallback: check response.data directly for various formats
      if (!isVendorSearch && response.data) {
        console.log('🔍 Checking response.data for vendors:', {
          isArray: Array.isArray(response.data),
          dataLength: response.data.length,
          hasAction1: !!response.data.action_1,
          hasVendors: !!response.data.vendors,
          sampleItem: response.data[0]
        });
        
        if (Array.isArray(response.data) && response.data.length > 0 && response.data[0].vendorType) {
          // Direct vendor array in response.data
          vendors = response.data;
          isVendorSearch = true;
          console.log('✅ Found vendors in direct response.data:', vendors.length);
        } else if (response.data.action_1 && Array.isArray(response.data.action_1)) {
          vendors = response.data.action_1;
          isVendorSearch = true;
          console.log('✅ Found vendors in response.data.action_1:', vendors.length);
        } else if (response.data.vendors && Array.isArray(response.data.vendors)) {
          vendors = response.data.vendors;
          isVendorSearch = true;
          console.log('✅ Found vendors in response.data.vendors:', vendors.length);
        } else {
          console.log('🔍 No vendor data found in response.data');
        }
      }
      
             // Handle Navigation Actions - Backend-driven with frontend fallback
       const handleNavigationActions = () => {
         let navigationHandled = false;

         // 1. PRIORITY: Task creation navigation
         if (response.actionPerformed === 'create_task' && response.data?.id) {
           console.log('🎯 Task creation detected, navigating to task board:', {
             taskId: response.data.id,
             taskName: response.data.name,
             fullData: response.data,
             actionPerformed: response.actionPerformed
           });
           
           // Store task creation context for highlighting and confetti
           const taskData = {
             taskId: response.data.id,
             taskName: response.data.name || response.data.title || 'New Task',
             createdAt: Date.now(),
             shouldHighlight: true,
             shouldShowConfetti: true,
             source: 'chat_creation'
           };
           
           console.log('🎯 Storing task data in sessionStorage:', taskData);
           sessionStorage.setItem('newTaskData', JSON.stringify(taskData));
           
                       // Skip immediate confetti, let KanbanBoard handle localized confetti
           
           setTimeout(() => {
             console.log('🔄 Navigating to task board after task creation');
             navigationService.navigateTo('/plan?tab=task-board');
           }, 800);
           navigationHandled = true;
         }

         // 1.1. PRIORITY: Calendar event creation navigation
         else if (response.actionPerformed === 'create_event' && response.data?.id) {
           console.log('🎯 Calendar event creation detected, navigating to calendar:', {
             eventId: response.data.id,
             eventTitle: response.data.title,
             fullData: response.data,
             actionPerformed: response.actionPerformed
           });
           
           // Store calendar event context for highlighting
           const eventData = {
             eventId: response.data.id,
             eventTitle: response.data.title || 'New Event',
             startTime: response.data.startTime,
             endTime: response.data.endTime,
             createdAt: Date.now(),
             shouldHighlight: true,
             source: 'chat_creation'
           };
           
           console.log('🎯 Storing event data in sessionStorage:', eventData);
           sessionStorage.setItem('newEventData', JSON.stringify(eventData));
           
           setTimeout(() => {
             console.log('🔄 Navigating to calendar after event creation');
             navigationService.navigateTo('/plan?tab=calendar');
           }, 800);
           navigationHandled = true;
         } else if (response.actionPerformed && response.actionPerformed.includes('task')) {
           console.log('🎯 Task-related action detected but not create_task:', {
             actionPerformed: response.actionPerformed,
             hasData: !!response.data,
             dataId: response.data?.id,
             dataKeys: response.data ? Object.keys(response.data) : null
           });
           
           // Fallback: Check if this is still a task creation with different naming
           if ((response.actionPerformed.includes('add') || response.actionPerformed.includes('create')) && 
               response.data && (response.data.id || response.data.taskId)) {
             console.log('🎯 Fallback task creation detected with different naming');
             
             const taskId = response.data.id || response.data.taskId;
             const taskData = {
               taskId: taskId,
               taskName: response.data.name || response.data.title || response.data.task || 'New Task',
               createdAt: Date.now(),
               shouldHighlight: true,
               shouldShowConfetti: true,
               source: 'chat_creation_fallback'
             };
             
             console.log('🎯 Storing fallback task data in sessionStorage:', taskData);
             sessionStorage.setItem('newTaskData', JSON.stringify(taskData));
             
             // Skip immediate confetti, let KanbanBoard handle localized confetti
             
             setTimeout(() => {
               console.log('🔄 Navigating to task board after fallback task creation');
               navigationService.navigateTo('/plan?tab=task-board');
             }, 800);
             navigationHandled = true;
           }
         }

         // 2. PRIORITY: New backend format with actionPerformed
         else if (response.actionPerformed === 'navigate_to_page' && response.actionParameters?.page) {
           const targetPage = response.actionParameters.page;
           console.log('🎯 New backend navigation format detected:', targetPage);
           
           // Map backend page names to routes
           const pageRouteMap = {
             'design-florals': '/design/florals',
             'design-decor': '/design/decor', 
             'design-cake': '/design/weddingcake',
             'design-attire': '/design/attire',
             'design-proposals': '/design/proposals',
             'design': '/design',
             'explore': '/explore',
             'plan': '/plan',
             'connect': '/connect'
           };
           
           const targetRoute = pageRouteMap[targetPage] || `/${targetPage}`;
           
           // Store comprehensive context for target page
           sessionStorage.setItem('aiPageData', JSON.stringify({
             data: response.data,
             query: text,
             aiResponse: response.message || response.text,
             navigationReason: response.actionParameters.reason || 'backend_navigation',
             actionType: 'navigate_to_page',
             targetPage: targetPage,
             timestamp: Date.now()
           }));
           
           setTimeout(() => {
             console.log('🔄 Executing new backend navigation to:', targetRoute);
             navigationService.navigateTo(targetRoute);
           }, 800);
           navigationHandled = true;
         }

         // 3. FALLBACK: Legacy backend format (response.actions array)
         else if (response.actions && response.actions.length > 0) {
           // Check for explicit navigation commands from backend
           const navigationAction = response.actions.find(action => 
             action.data?.route
           );
           
           if (navigationAction) {
             const route = navigationAction.data?.route;
             if (route) {
               console.log('🎯 Legacy backend navigation action detected:', route);
               
               // Store comprehensive context for target page
               sessionStorage.setItem('aiPageData', JSON.stringify({
                 data: navigationAction.data?.pageData || navigationAction.data,
                 query: text,
                 aiResponse: response.text,
                 navigationReason: 'legacy_backend_action',
                 actionType: navigationAction.type,
                 timestamp: Date.now()
               }));
               
               setTimeout(() => {
                 console.log('🔄 Executing legacy backend navigation to:', route);
                 navigationService.navigateTo(route);
               }, 800);
               navigationHandled = true;
             }
           }

           // Check for page-specific actions from backend
           if (!navigationHandled) {
             const pageActions = response.actions.filter(action => 
               ['explore', 'design', 'decor', 'florals', 'cake', 'attire', 'proposals'].includes(action.type)
             );
             
             if (pageActions.length > 0) {
               const primaryAction = pageActions[0];
               console.log('🎯 Backend page action detected:', primaryAction.type);
               
               const routeMap = {
                 'explore': '/explore',
                 'design': '/design',
                 'decor': '/design/decor',
                 'florals': '/design/florals',
                 'cake': '/design/weddingcake',
                 'attire': '/design/attire',
                 'proposals': '/design/proposals'
               };
               
               const targetRoute = routeMap[primaryAction.type];
               if (targetRoute) {
                 sessionStorage.setItem('aiPageData', JSON.stringify({
                   data: primaryAction.data,
                   query: text,
                   aiResponse: response.text,
                   navigationReason: 'backend_action',
                   actionType: primaryAction.type,
                   timestamp: Date.now()
                 }));
                 
                 setTimeout(() => {
                   console.log('🔄 Executing backend page navigation to:', targetRoute);
                   navigationService.navigateTo(targetRoute);
                 }, 800);
                 navigationHandled = true;
               }
             }
           }
         }

         // 4. Handle vendor search results (existing functionality)
         if (!navigationHandled && isVendorSearch && vendors.length > 0) {
           console.log('🔍 Vendor search results detected! Vendors found:', vendors.length);
           
           const searchResults = {
             vendors: vendors,
             query: text,
             aiResponse: response.text,
             totalResults: vendors.length
           };
           sessionStorage.setItem('aiSearchResults', JSON.stringify(searchResults));
           
           setTimeout(() => {
             console.log('🔄 Executing vendor search navigation to /connect/search');
             navigationService.navigateTo('/connect/search');
           }, 800);
           navigationHandled = true;
         }

         // 5. FALLBACK: Frontend keyword detection (for robustness)
         if (!navigationHandled) {
           console.log('🔄 Trying frontend fallback keyword detection...');
           const lowerText = text.toLowerCase();
           const lowerResponse = response.text?.toLowerCase() || '';
           
           // High-confidence navigation keywords
           const navigationKeywords = [
             { keywords: ['show venues', 'browse venues', 'explore venues', 'venue gallery'], route: '/explore', confidence: 'high' },
             { keywords: ['design options', 'start designing', 'design my wedding', 'design hub'], route: '/design', confidence: 'high' },
             { keywords: ['decor ideas', 'decorations', 'table settings', 'centerpieces'], route: '/design/decor', confidence: 'medium' },
             { keywords: ['flower arrangements', 'floral designs', 'bouquet ideas'], route: '/design/florals', confidence: 'medium' },
             { keywords: ['wedding cake designs', 'cake options', 'cake gallery'], route: '/design/weddingcake', confidence: 'medium' },
             { keywords: ['wedding attire', 'dress ideas', 'suit options'], route: '/design/attire', confidence: 'medium' },
             { keywords: ['proposal planning', 'engagement ideas'], route: '/design/proposals', confidence: 'medium' }
           ];
           
           // Only navigate on high-confidence matches to avoid false positives
           for (const navItem of navigationKeywords) {
             const hasStrongMatch = navItem.keywords.some(keyword => 
               lowerText.includes(keyword) || 
               (navItem.confidence === 'high' && lowerResponse.includes(keyword))
             );
             
             if (hasStrongMatch) {
               console.log('🔍 Frontend fallback navigation detected:', navItem.route);
               
               sessionStorage.setItem('aiPageData', JSON.stringify({
                 query: text,
                 aiResponse: response.text,
                 navigationReason: 'frontend_fallback',
                 confidence: navItem.confidence,
                 matchedKeywords: navItem.keywords.filter(k => 
                   lowerText.includes(k) || lowerResponse.includes(k)
                 ),
                 timestamp: Date.now()
               }));
               
               setTimeout(() => {
                 console.log('🔄 Executing frontend fallback navigation to:', navItem.route);
                 navigationService.navigateTo(navItem.route);
               }, 1200); // Longer delay for fallback
               navigationHandled = true;
               break;
             }
           }
         }

         if (!navigationHandled) {
           console.log('ℹ️ No navigation intent detected');
         }
       };

      // Execute navigation logic
      handleNavigationActions();

      // Execute any actions returned from the backend (for UI updates only)
      if (response.actions && response.actions.length > 0) {
        console.log('🎯 Processing agent actions:', response.actions);
        
        const actions = response.actions || [];
        console.log('🎯 Adding actions to pending actions:', actions.length);
        set((state) => ({
          pendingActions: [...state.pendingActions, ...actions]
        }));
        
        // Execute actions for UI updates/notifications
        console.log('⚡ Starting action execution loop...');
        for (const action of actions) {
          console.log('⚡ Executing action:', {
            type: action.type,
            hasData: !!action.data,
            operation: action.data?.operation
          });
          await store.executeAction(action);
        }

        // Trigger a global refresh for all plan components after action execution
        console.log('🔄 Triggering global refresh after actions');
        setTimeout(() => {
          globalRefreshEmitter.emitRefresh('global', {
            type: 'refresh',
            data: { source: 'chat', timestamp: Date.now(), actions: actions }
          } as AgentAction);
        }, 100);
      } else {
        console.log('ℹ️ No actions returned from backend');
        
        // Even if no actions, check if this might be a budget/calendar/task related message
        // and trigger a manual refresh to catch any backend changes
        const lowerText = text.toLowerCase();
        if (lowerText.includes('budget') || lowerText.includes('$') || lowerText.includes('cost')) {
          console.log('💰 Budget-related message detected, triggering budget refresh');
          setTimeout(() => {
            globalRefreshEmitter.emitRefresh('budget', {
              type: 'budget',
              data: { source: 'chat_manual', message: text }
            } as AgentAction);
          }, 100);
        } else if (lowerText.includes('schedule') || lowerText.includes('appointment') || lowerText.includes('calendar')) {
          console.log('📅 Calendar-related message detected, triggering calendar refresh');
          setTimeout(() => {
            globalRefreshEmitter.emitRefresh('calendar', {
              type: 'calendar',
              data: { source: 'chat_manual', message: text }
            } as AgentAction);
          }, 100);
        } else if (lowerText.includes('task') || lowerText.includes('todo') || lowerText.includes('remind')) {
          console.log('✅ Task-related message detected, triggering task refresh');
          setTimeout(() => {
            globalRefreshEmitter.emitRefresh('task', {
              type: 'task',
              data: { source: 'chat_manual', message: text }
            } as AgentAction);
          }, 100);
        }
      }

    } catch (error: any) {
      console.error('❌ Chat error occurred:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        type: typeof error
      });
      
      // Set user-friendly error message
      let errorMessage = "I'm sorry, I encountered an error processing your request.";
      
      if (error.message?.includes('fetch')) {
        errorMessage = "Unable to connect to the wedding planning service. Please check your internet connection.";
        console.log('🔗 Network error detected');
      } else if (error.message?.includes('Backend API error')) {
        errorMessage = "The wedding planning service is temporarily unavailable. Please try again in a moment.";
        console.log('🚨 Backend API error detected');
      } else if (error.message?.includes('timeout')) {
        errorMessage = "The request timed out. Please try again with a shorter message.";
        console.log('⏱️ Timeout error detected');
      } else {
        console.log('❓ Unknown error type detected');
      }
      
      console.log('🔧 Setting error message:', errorMessage);
      store.setError(errorMessage);
      
      // Add error message to chat for user visibility
      const fullErrorMessage = `${errorMessage} You can manually add items to your calendar, tasks, or budget in the planning section while I'm unavailable.`;
      console.log('💬 Adding error message to chat:', fullErrorMessage);
      store.addMessage({
        text: fullErrorMessage,
        isUser: false
      });
    } finally {
      console.log('🔧 Setting loading to false');
      store.setLoading(false);
    }
  },

  executeAction: async (action: AgentAction) => {
    try {
      console.log('🔧 executeAction called with:', {
        type: action.type,
        hasData: !!action.data,
        actionData: action.data
      });
      
      const agentService = AgentService.getInstance();
      console.log('🔧 Calling agentService.executeAction...');
      const success = await agentService.executeAction(action);
      console.log('🔧 agentService.executeAction returned:', success);
      
      if (success) {
        console.log('✅ Action executed successfully:', action.type);
        set((state) => ({
          pendingActions: state.pendingActions.map(a => 
            a === action ? { ...a, executed: true } : a
          )
        }));

        // Handle task creation navigation and confetti
        if (action.type === 'task' && action.data?.operation === 'create_task') {
          console.log('🎯 Task creation action detected, setting up navigation and confetti:', action.data);
          
          // Store task creation context for highlighting and confetti
          const taskData = {
            taskId: action.data.id || action.data.taskId,
            taskName: action.data.title || action.data.name || 'New Task',
            createdAt: Date.now(),
            shouldHighlight: true,
            shouldShowConfetti: true,
            source: 'chat_creation_action'
          };
          
          console.log('🎯 Storing task data in sessionStorage:', taskData);
          sessionStorage.setItem('newTaskData', JSON.stringify(taskData));
          
          // Skip immediate confetti, let KanbanBoard handle localized confetti
          
          // Navigate to task board
          setTimeout(() => {
            console.log('🔄 Navigating to task board after task creation');
            navigationService.navigateTo('/plan?tab=task-board');
          }, 800);
        }

        // Emit a specific refresh event for this action type
        console.log('🔄 Emitting specific refresh for:', action.type);
        globalRefreshEmitter.emitRefresh(action.type, action);
        
      } else {
        console.warn('⚠️ Action execution failed:', action);
      }
    } catch (error) {
      console.error('❌ Error executing action:', error);
      console.error('❌ executeAction error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error; // Re-throw to see if this is causing the main error
    }
  },

  clearPendingActions: () => set({ pendingActions: [] }),

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  loadConversationHistory: async () => {
    const store = get();
    
    // Check if we already have persisted messages (more than just welcome message)
    const hasPersistedHistory = store.messages.length > 1 && 
                                store.messages.some(m => !m.text.includes("wedding planning assistant"));
    
    if (store.isHistoryLoaded || store.isHistoryLoading || hasPersistedHistory) {
      console.log('📚 History already loaded/persisted or loading, skipping API call...');
      if (hasPersistedHistory) {
        set({ isHistoryLoaded: true });
      }
      return;
    }

    try {
      console.log('📚 Loading fresh conversation history from API...');
      set({ isHistoryLoading: true, error: null });

      const historyService = ConversationHistoryService.getInstance();
      const historyMessages = await historyService.fetchConversationHistory(50);

      if (historyMessages.length > 0) {
        console.log('📚 Loaded', historyMessages.length, 'messages from API');

        // Convert history messages to chat messages with unique IDs
        const chatMessages: ChatMessage[] = historyMessages.map((msg, index) => ({
          id: msg.id || `history-${msg.timestamp}-${index}`, // Ensure unique IDs
          text: msg.message || msg.text,
          isUser: msg.isUser,
          timestamp: msg.timestamp
        }));

        // Add contextual welcome message
        const welcomeMessage: ChatMessage = {
          id: `welcome-back-${Date.now()}`,
          text: "Welcome back! I can see our previous conversation. How can I help you continue with your wedding planning? 💝",
          isUser: false,
          timestamp: Date.now(),
        };

        // Replace all messages with history + welcome
        set({ 
          messages: [...chatMessages, welcomeMessage],
          isHistoryLoaded: true,
          isHistoryLoading: false 
        });
      } else {
        console.log('📚 No conversation history found, keeping default welcome message');
        // Keep the existing welcome message if no history
        set({ 
          isHistoryLoaded: true,
          isHistoryLoading: false 
        });
      }

    } catch (error) {
      console.error('❌ Error loading conversation history:', error);
      set({ 
        error: 'Failed to load conversation history',
        isHistoryLoaded: true, // Mark as loaded to prevent retries
        isHistoryLoading: false 
      });
    }
  },
    }),
    {
      name: 'wedify-chat-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        messages: state.messages,
        isHistoryLoaded: state.isHistoryLoaded,
      }),
    }
  )
);